import { ChangeEvent, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useDebounce } from '@uidotdev/usehooks';
import { AnnouncementsTable } from './AnnouncementsTable/AnnouncementsTable';
import { Board } from '~/components/Shared/Layout/Board/Board';
import { ErrorMessage } from '~/components/Shared/ErrorMessage/ErrorMessage';
import { Button, InputBar, Pagination } from '~/ui';
import { TableSelect } from '~/ui/TableSelect/TableSelect';
import '~/assets/css/select-table.css';
import styles from './AnnouncementsBoard.module.scss';
import { TableAmountPage } from '~/ui/TableAmountPage/TableAmountPage';
import { useLocalStorage } from '~/utils/useLocalStorage';
import { useTranslation } from 'react-i18next';
import { useToastError } from '~/utils/useToastError';
import { getPaginate } from '~/utils/getPaginate';
import { PageTransition } from '~/components/Shared/PageTransition/PageTransition';

interface SelectedPage {
  selected: number;
}

interface Points {
  page: number;
}

interface Props {
  statusFilter?: string;
}

export const AnnouncementsBoard = ({ statusFilter }: Props) => {
  const { initialValues, storedInitialValues, create } =
    useLocalStorage<Points>({
      initialValues: { page: 15 },
      key: 'announcement-points',
      exclude: [],
    });
  const [sort, setSort] = useState({
    field: '',
    direction: '',
  });
  const [perPage, setPerPage] = useState(
    storedInitialValues?.page || initialValues.page
  );
  const { paginate } = getPaginate();
  const [page, setPage] = useState<number>(paginate);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const toastError = useToastError();
  const client = useQueryClient();
  const { t } = useTranslation();

  useEffect(() => {
    create({ page: perPage });
  }, [perPage]);

  const params = {
    sort: sort.direction,
    perPage,
    field: sort.field,
    query: debouncedSearchTerm,
    page,
    ...(statusFilter ? { status_id: statusFilter } : {}),
  };

  const {
    data: announcements,
    isError,
    isLoading: isDataLoading,
    error,
  } = useQuery({
    queryFn: async () => {
      const response = await instance.get('admin/announcement', {
        params,
      });
      return response.data;
    },
    queryKey: ['announcement', params],
    keepPreviousData: true,
  });

  const handleSelect = (id: number) => {
    if (selectedItems.includes(id)) {
      const filteredUsers = selectedItems.filter(
        selectedId => selectedId !== id
      );
      setSelectedItems(filteredUsers);
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const handleToggleAll = () => {
    if (selectedItems.length === announcements?.data.length) {
      setSelectedItems([]);
    } else {
      const userIdArray = announcements?.data.map((user: any) => user.id);
      setSelectedItems(userIdArray);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await instance.delete('admin/announcement-many', {
        data: { announcement_ids: selectedItems },
      });
      setSelectedItems([]);
      client.invalidateQueries(['announcement']);
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <Board>
      <div className={styles.announcementsTableOptions}>
        <TableSelect perPage={perPage} setPerPage={setPerPage} />
        <div className={styles.tableHeadRightWrapper}>
          {!!selectedItems.length && (
            <Button
              text={t('delete')}
              color="red"
              onClick={handleDeleteSelected}
              className={styles.deleteButton}
            />
          )}
          <InputBar
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            value={searchTerm}
            placeholder={t('search_by_title')}
            classNameInput={styles.announcementsTableInput}
            label={`${t('search')}:`}
            star={false}
            classNameInputWrapper={styles.announcementsTableInputWrapper}
          />
        </div>
      </div>
      <PageTransition page={page}>
        {isError ? (
          <ErrorMessage error={error} />
        ) : (
          <AnnouncementsTable
            announcements={announcements?.data || []}
            sort={sort}
            setSort={setSort}
            selectedItems={selectedItems}
            handleToggle={handleSelect}
            handleToggleAll={handleToggleAll}
            isLoading={isDataLoading}
          />
        )}
      </PageTransition>
      <div className={styles.announcementsTablePagination}>
        <TableAmountPage
          firstRow={announcements?.from}
          lastRow={announcements?.to}
          total={announcements?.total}
        />
        <Pagination
          forcePage={paginate}
          pageCount={announcements?.last_page}
          onPageChange={(selectedPage: SelectedPage) => {
            setPage(selectedPage.selected + 1);
          }}
        />
      </div>
    </Board>
  );
};
